import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FaUsers,
  FaProjectDiagram,
  FaUserShield,
  FaCalendar,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimes,
  FaClock,
  FaEnvelope
} from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface DashboardStats {
  totalUsers: number;
  totalProjects: number;
  adminUsers: number;
  paidKitProjects?: number;
}

interface UserAnalytics {
  total: number;
  active: number;
  disabled: number;
  deleted: number;
  verified: number;
  unverified: number;
  byRole: {
    agency_admin: number;
    agency_member: number;
    member: number;
    null: number;
  };
}

interface ProjectAnalytics {
  total: number;
  published: number;
  draft: number;
  deleted: number;
  byStatus: {
    created: number;
    core_questions_complete: number;
    follow_up_generated: number;
    platform_info_created: number;
    persona_created: number;
    value_props_created: number;
    features_generated: number;
    features_prioritized: number;
    requirements_generated: number;
    document_generated: number;
  };
}

interface DocumentAnalytics {
  totalVersions: number;
  projectsWithDocuments: number;
}

interface OrganizationAnalytics {
  totalInvites: number;
  pendingInvites: number;
  acceptedInvites: number;
  expiredInvites: number;
}

interface GrowthData {
  _id: {
    year: number;
    month: number;
  };
  count: number;
}

interface TopIndustry {
  _id: string;
  count: number;
}

interface RecentUser {
  _id: string;
  name: string;
  email: string;
  createdAt: string;
  role?: string;
}

interface RecentProject {
  _id: string;
  projectName: string;
  userId: {
    name: string;
    email: string;
  };
  createdAt: string;
  projectStatus?: string;
  status?: string;
}

interface DashboardData {
  stats: DashboardStats;
  analytics: {
    users: UserAnalytics;
    projects: ProjectAnalytics;
    documents: DocumentAnalytics;
    organizations: OrganizationAnalytics;
  };
  recentUsers: RecentUser[];
  recentProjects: RecentProject[];
  growth: {
    users: GrowthData[];
    projects: GrowthData[];
  };
  topIndustries: TopIndustry[];
}

const PROJECT_STATUS_LABELS: Record<string, string> = {
  created: 'Created',
  core_questions_complete: 'Core Questions',
  follow_up_generated: 'Follow-up Generated',
  platform_info_created: 'Platform Info',
  persona_created: 'Personas Created',
  value_props_created: 'Value Props',
  features_generated: 'Features Generated',
  features_prioritized: 'Features Prioritized',
  requirements_generated: 'Requirements',
  document_generated: 'Document Generated',
};

const PROJECT_STATUS_COLORS: Record<string, string> = {
  created: 'bg-gray-200',
  core_questions_complete: 'bg-blue-300',
  follow_up_generated: 'bg-blue-400',
  platform_info_created: 'bg-indigo-400',
  persona_created: 'bg-purple-400',
  value_props_created: 'bg-pink-400',
  features_generated: 'bg-orange-400',
  features_prioritized: 'bg-yellow-400',
  requirements_generated: 'bg-lime-400',
  document_generated: 'bg-green-500',
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const { data: dashboardData, isLoading: isDashboardLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const response = await api.get('/admin/dashboard');
      return (response.data.data || response.data) as DashboardData;
    },
  });

  const { data: paidKitCount = 0 } = useQuery({
    queryKey: ['dashboard-paid-kit'],
    queryFn: async () => {
      const res = await api.get('/admin/projects', { params: { limit: 1 } });
      const data = res.data?.data || res.data;
      return (data?.filterCounts?.founderKitPaid ?? 0) as number;
    },
  });

  const isLoading = isDashboardLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {
    totalUsers: 0,
    totalProjects: 0,
    adminUsers: 0
  };

  const analytics = dashboardData?.analytics || {
    users: {
      total: 0,
      active: 0,
      disabled: 0,
      deleted: 0,
      verified: 0,
      unverified: 0,
      byRole: { agency_admin: 0, agency_member: 0, member: 0, null: 0 }
    },
    projects: {
      total: 0,
      published: 0,
      draft: 0,
      deleted: 0,
      byStatus: {
        created: 0, core_questions_complete: 0, follow_up_generated: 0,
        platform_info_created: 0, persona_created: 0, value_props_created: 0,
        features_generated: 0, features_prioritized: 0, requirements_generated: 0, document_generated: 0
      }
    },
    documents: { totalVersions: 0, projectsWithDocuments: 0 },
    organizations: { totalInvites: 0, pendingInvites: 0, acceptedInvites: 0, expiredInvites: 0 }
  };

  const recentUsers = dashboardData?.recentUsers || [];
  const recentProjects = dashboardData?.recentProjects || [];
  const topIndustries = dashboardData?.topIndustries || [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  return (
    <div className="space-y-6 w-full max-w-full overflow-hidden">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary to-primaryDark rounded-2xl p-4 sm:p-6 lg:p-8 text-white w-full"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 truncate">Welcome back, Admin!</h1>
            <p className="text-primaryLight text-sm sm:text-base lg:text-lg">Here's what's happening with your platform today.</p>
          </div>
          <div className="hidden lg:block flex-shrink-0">
            <FaUsers className="text-4xl lg:text-6xl text-white opacity-20" />
          </div>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow w-full"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className="text-sm font-medium text-gray-600 mb-1 truncate">Total Users</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
            <div className="p-2 lg:p-3 rounded-full bg-blue-500 bg-opacity-10 flex-shrink-0">
              <FaUsers className="h-6 w-6 lg:h-8 lg:w-8 text-blue-500" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow w-full"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className="text-sm font-medium text-gray-600 mb-1 truncate">Total Projects</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.totalProjects}</p>
            </div>
            <div className="p-2 lg:p-3 rounded-full bg-green-500 bg-opacity-10 flex-shrink-0">
              <FaProjectDiagram className="h-6 w-6 lg:h-8 lg:w-8 text-green-500" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow w-full"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className="text-sm font-medium text-gray-600 mb-1 truncate">Admin Users</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{stats.adminUsers}</p>
              <p className="text-xs text-blue-600 mt-1">System administrators</p>
            </div>
            <div className="p-2 lg:p-3 rounded-full bg-purple-500 bg-opacity-10 flex-shrink-0">
              <FaUserShield className="h-6 w-6 lg:h-8 lg:w-8 text-purple-500" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 hover:shadow-xl transition-shadow w-full"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0 pr-2">
              <p className="text-sm font-medium text-gray-600 mb-1 truncate">Paid Kits</p>
              <p className="text-2xl lg:text-3xl font-bold text-gray-900">{paidKitCount}</p>
              <p className="text-xs text-green-600 mt-1">Projects with paid founder kit</p>
            </div>
            <div className="p-2 lg:p-3 rounded-full bg-green-500 bg-opacity-10 flex-shrink-0">
              <FaCheckCircle className="h-6 w-6 lg:h-8 lg:w-8 text-green-500" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 w-full">
        {/* User Analytics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">User Analytics</h3>
            <FaUsers className="h-5 w-5 text-primary" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <FaCheckCircle className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-sm font-medium text-gray-600">Active</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.users.active}</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <FaTimes className="h-4 w-4 text-red-500 mr-2" />
                <span className="text-sm font-medium text-gray-600">Disabled</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.users.disabled}</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <div className="flex items-center justify-center mb-2">
                <FaExclamationTriangle className="h-4 w-4 text-orange-500 mr-2" />
                <span className="text-sm font-medium text-gray-600">Deleted</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{analytics.users.deleted ?? 0}</p>
            </div>
          </div>
          
        </motion.div>

        {/* Project Analytics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Project Analytics</h3>
            <FaProjectDiagram className="h-5 w-5 text-primary" />
          </div>
          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="flex items-center justify-center mb-1">
                <FaCheckCircle className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-xs font-medium text-gray-600">Published</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{analytics.projects.published}</p>
            </div>
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-center mb-1">
                <FaClock className="h-3 w-3 text-blue-500 mr-1" />
                <span className="text-xs font-medium text-gray-600">Draft</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{analytics.projects.draft}</p>
            </div>
            <div className="text-center p-3 bg-red-50 rounded-lg">
              <div className="flex items-center justify-center mb-1">
                <FaTimes className="h-3 w-3 text-red-500 mr-1" />
                <span className="text-xs font-medium text-gray-600">Deleted</span>
              </div>
              <p className="text-xl font-bold text-gray-900">{analytics.projects.deleted}</p>
            </div>
          </div>

          {/* Project Status Breakdown */}
          <div>
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Project Status Breakdown</h4>
            <div className="space-y-2.5">
              {(Object.entries(analytics.projects.byStatus) as [string, number][]).map(([status, count]) => {
                const total = analytics.projects.total || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={status}>
                    <div className="flex justify-between items-center mb-0.5">
                      <span className="text-xs text-gray-600">{PROJECT_STATUS_LABELS[status] || status}</span>
                      <span className="text-xs font-semibold text-gray-900">{count}</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${PROJECT_STATUS_COLORS[status] || 'bg-gray-400'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Top Industries */}
      {topIndustries.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Top Industries</h3>
            <FaProjectDiagram className="h-5 w-5 text-primary" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {topIndustries.slice(0, 6).map((industry) => (
              <div key={industry._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm font-medium text-gray-900 truncate">{industry._id}</span>
                <span className="text-sm font-bold text-primary">{industry.count}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 w-full">
        {/* Recent Users */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full min-w-0"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Recent Users</h3>
            <FaUsers className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-4">
            {recentUsers.map((user, index) => (
              <motion.div
                key={user._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-primary to-primaryDark rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {getInitials(user.name)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  {user.role && (
                    <p className="text-xs text-blue-600 capitalize">{user.role.replace('_', ' ')}</p>
                  )}
                </div>
                <div className="flex items-center text-xs text-gray-400">
                  <FaCalendar className="mr-1" />
                  {formatDate(user.createdAt)}
                </div>
              </motion.div>
            ))}
            {recentUsers.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FaUsers className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No recent users</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Projects */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full min-w-0"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">Recent Projects</h3>
            <FaProjectDiagram className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-4">
            {recentProjects.map((project, index) => (
              <motion.div
                key={project._id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="flex items-center space-x-4 p-3 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center text-white">
                  <FaProjectDiagram className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{project.projectName}</p>
                  <p className="text-xs text-gray-500 truncate">by {project.userId.name}</p>
                  {project.projectStatus && (
                    <p className="text-xs text-green-600 capitalize">{project.projectStatus.replace(/_/g, ' ')}</p>
                  )}
                </div>
                <div className="flex items-center text-xs text-gray-400">
                  <FaCalendar className="mr-1" />
                  {formatDate(project.createdAt)}
                </div>
              </motion.div>
            ))}
            {recentProjects.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FaProjectDiagram className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No recent projects</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.0 }}
        className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 w-full"
      >
        <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-4 lg:mb-6">Quick Navigation</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 w-full">
          <button 
            onClick={() => navigate('/users')}
            className="flex items-center justify-center p-3 lg:p-4 border-2 border-primary rounded-lg text-primary hover:bg-primary hover:text-white transition-colors w-full min-w-0"
          >
            <FaUsers className="mr-2 lg:mr-3 h-4 w-4 lg:h-5 lg:w-5 flex-shrink-0" />
            <span className="font-medium text-sm lg:text-base truncate">Manage Users</span>
          </button>
          <button 
            onClick={() => navigate('/projects')}
            className="flex items-center justify-center p-3 lg:p-4 border-2 border-green-500 rounded-lg text-green-500 hover:bg-green-500 hover:text-white transition-colors w-full min-w-0"
          >
            <FaProjectDiagram className="mr-2 lg:mr-3 h-4 w-4 lg:h-5 lg:w-5 flex-shrink-0" />
            <span className="font-medium text-sm lg:text-base truncate">View Projects</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default Dashboard; 